import type { Currency } from "@/types";
import { formatCurrency } from "../cashflow/CashflowContext";
import { formatAppDate } from "../../utils/date";

export const GRID_LINE = "border-slate-200/70";

export const SPREADSHEET_ROW =
  "grid grid-cols-[minmax(0,1fr)_6.5rem_7.5rem] items-stretch";

export const SPREADSHEET_CELL = `border-r ${GRID_LINE} px-3 py-2`;

export const SPREADSHEET_AMOUNT_CELL = `px-3 py-2 text-right`;

type Props = {
  description: string;
  date: string;
  amount: number;
  currency: Currency;
  subtitle?: string | null;
  className?: string;
  highlight?: boolean;
};

export function TransactionSpreadsheetRow({
  description,
  date,
  amount,
  currency,
  subtitle,
  className = "",
  highlight = false,
}: Props) {
  const amountClass = amount >= 0 ? "text-emerald-700" : "text-red-600";

  return (
    <div
      className={`${SPREADSHEET_ROW} h-full ${highlight ? "bg-emerald-50/50" : ""} ${className}`}
    >
      <div className={`${SPREADSHEET_CELL} flex min-w-0 items-center`}>
        <div className="min-w-0">
          <p className="truncate text-sm text-slate-900">{description}</p>
          {subtitle ? (
            <p className="truncate text-xs text-slate-500">{subtitle}</p>
          ) : null}
        </div>
      </div>
      <div
        className={`${SPREADSHEET_CELL} flex items-center text-xs text-slate-500`}
      >
        {formatAppDate(date)}
      </div>
      <div className={`${SPREADSHEET_AMOUNT_CELL} flex items-center justify-end`}>
        <p className={`text-sm font-medium tabular-nums ${amountClass}`}>
          {formatCurrency(amount, currency)}
        </p>
      </div>
    </div>
  );
}

export const SPREADSHEET_COLUMNS = [
  { label: "Description" },
  { label: "Date" },
  { label: "Amount", align: "right" as const },
];

export function SpreadsheetHeaderRow({
  columns = SPREADSHEET_COLUMNS,
}: {
  columns?: { label: string; align?: "left" | "right"; className?: string }[];
}) {
  return (
    <div
      className={`${SPREADSHEET_ROW} border-b ${GRID_LINE} bg-slate-50/50 text-xs font-medium text-slate-500`}
    >
      {columns.map((column, index) => {
        const isLast = index === columns.length - 1;
        const align =
          column.align === "right" ? "justify-end text-right" : "text-left";
        return (
          <div
            key={column.label}
            className={`${isLast ? "px-3 py-2" : SPREADSHEET_CELL} flex items-center ${align} ${column.className ?? ""}`}
          >
            {column.label}
          </div>
        );
      })}
    </div>
  );
}
